/* eslint-disable dot-notation */
import dynamic from 'next/dynamic';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import screenfull from 'screenfull';
import styled, { css } from 'styled-components';

import { getTimeDisplay } from 'shared/utils/getTimeDisplay';
import { Image } from 'shared/components/Image';
import { IVideo } from 'editorial/types/ArticleVideo';
import { Pause } from 'shared/icons/Pause';
import { PAUSE_BUTTON_Z_INDEX, VIDEO_CONTROLS_Z_INDEX } from 'shared/enums/VideoEnums';
import { pauseAllAudioAndVideo } from 'shared/utils/pauseAllAudioAndVideo';
import { Play } from 'shared/icons/Play';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useIsMounted } from 'shared/hooks/useIsMounted';
import { variableFontRegular } from 'styles/theme/text';

const VolumeControl = dynamic(
  () => import('shared/components/VolumeControl').then((mod) => mod.VolumeControl),
  { ssr: false },
);

interface ISpaceFillingVideoProps {
  height: string;
  eventLocation: string;
  id?: string | number;
  isAutoplayHeaderVideo?: boolean;
  theme?: 'light' | 'dark';
  width: string;
}

export const SpaceFillingVideo = ({
  eventLocation,
  height,
  id,
  isAutoplayHeaderVideo = false,
  poster = {},
  theme,
  url,
  width,
}: IVideo & ISpaceFillingVideoProps) => {
  const isMounted = useIsMounted();
  const { largeUrl = null } = poster;
  const playerRef = useRef(null);
  const videoContainerRef = useRef(null);
  const [isShowingCoverPicture, setIsShowingCoverPicture] = useState(
    !isAutoplayHeaderVideo && !!largeUrl,
  );
  const [showControls, setShowControls] = useState(!isAutoplayHeaderVideo);
  const [isPlaying, setIsPlaying] = useState(isAutoplayHeaderVideo);
  const [playCount, setPlayCount] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showGiantPlayButton, setShowGiantPlayButton] = useState(true);
  const [volume, setVolume] = useState(100);
  const [showVolumeControl, setShowVolumeControl] = useState(true);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);

  const onPlay = () => {
    pauseAllAudioAndVideo();

    if (playerRef?.current) {
      playerRef?.current?.play();
    }

    if (isShowingCoverPicture) {
      setIsShowingCoverPicture(false);
    }

    if (playCount === 0 && !isAutoplayHeaderVideo) {
      setPlayCount(1);
    }
  };

  const onBigPlayButtonClick = (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    onPlay();
    setShowGiantPlayButton(false);
  };

  const onPause = () => {
    if (playerRef?.current) {
      playerRef.current?.pause();
    }
  };

  const handleCloseFullScreen = () => {
    if (screenfull.isEnabled) {
      void screenfull.toggle();
      setIsFullScreen(false);
    }
  };

  const handleSeekMouseUp = () => {
    if (playerRef?.current?.paused) {
      pauseAllAudioAndVideo();
      playerRef.current?.play();
    }
  };

  const handleSeekChange = (e) => {
    if (playerRef.current) {
      playerRef.current?.pause();
      playerRef.current.currentTime = Number(e.target.value);
      setProgress(playerRef.current.currentTime);
    }
  };

  const handleEnded = () => {
    setShowGiantPlayButton(true);
    sendTrackingEvent('content_end_reached', {
      picture_id: id.toString(),
      event_context: eventLocation,
    });

    if (!isAutoplayHeaderVideo) {
      if (isFullScreen) {
        handleCloseFullScreen();
      }
      if (largeUrl) {
        setIsShowingCoverPicture(true);
      }
    }
  };

  const handleShowControl = () => {
    setShowControls(true);
  };

  const handleHideControl = () => {
    setTimeout(() => {
      setShowControls(false);
    }, 5000);
  };

  const handleVolumeChange = (val) => {
    if (playerRef.current) {
      playerRef.current.volume = val / 100; /* volume is between 0 - 1 */
      setVolume(val);
    }
  };

  const handleVolumeIconClick = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!playerRef.current) {
      return;
    }

    if (playerRef.current.muted) {
      playerRef.current.muted = false;
      handleVolumeChange(100);
      setShowVolumeControl(true);
    } else {
      playerRef.current.muted = true;
      handleVolumeChange(0);
      setShowVolumeControl(false);
    }
  };

  const setVideoData = useCallback(() => {
    setDuration(playerRef?.current?.duration);
    setProgress(playerRef?.current?.currentTime);

    if (playerRef?.current?.ended) {
      handleEnded();
    }
  }, [playerRef.current?.duration, playerRef.current?.currentTime, playerRef.current?.ended]);

  useEffect(() => {
    if (isMounted()) {
      setShowControls(true);
    }
  }, []);

  useEffect(() => {
    const handlePlay = () => {
      // we have to use a state value here because Safari does not get the updated value
      setIsPlaying(true);
      pauseAllAudioAndVideo(playerRef);
      sendTrackingEvent('video_card_controls_tap', {
        picture_id: id.toString(),
        action_type: 'play',
      });
    };

    const handlePause = () => {
      setIsPlaying(false);
      // the pause event triggers when the video ends, so to prevent tracking the  `pause` event,
      // check if we're at the end of the video
      if (!playerRef?.current?.ended) {
        sendTrackingEvent('video_card_controls_tap', {
          picture_id: id.toString(),
          action_type: 'pause',
        });
      }
    };

    playerRef?.current?.addEventListener('play', handlePlay);
    playerRef?.current?.addEventListener('pause', handlePause);

    return () => {
      playerRef?.current?.removeEventListener('play', handlePlay);
      playerRef?.current?.removeEventListener('pause', handlePause);
    };
  }, []);

  useEffect(() => {
    playerRef?.current?.addEventListener('durationchange', setVideoData);
    playerRef?.current?.addEventListener('loadeddata', setVideoData);
    playerRef?.current?.addEventListener('timeupdate', setVideoData);

    return () => {
      playerRef?.current?.addEventListener('durationchange', setVideoData);
      playerRef?.current?.addEventListener('loadeddata', setVideoData);
      playerRef?.current?.addEventListener('timeupdate', setVideoData);
    };
  }, []);

  return (
    <SpaceFillingVideo.Container
      data-qa="long_form_video"
      onClick={isPlaying ? onPause : onPlay}
      onMouseOver={handleShowControl}
      onMouseLeave={handleHideControl}
      onTouchStart={handleShowControl}
      onTouchEnd={handleHideControl}
      onFocus={handleShowControl}
      ref={videoContainerRef}
      $theme={theme}
    >
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        controls={
          false
        } /* we are using the native controls for mobile because iOS mobile does not support the Fullscreen API */
        controlsList="nodownload"
        data-qa="long_form_video_react_player"
        height={height}
        loop={isAutoplayHeaderVideo}
        playsInline
        poster={largeUrl}
        ref={playerRef}
        src={`${url}${
          largeUrl ? '' : '#t=0.1'
        }`} /* this sets the first frame as the poster image if it does not exist */
        width={width}
        id={id.toString()}
      >
        <source src={url} type="video/mp4" />
        <source src={url} type="video/ogg" />
        Your browser does not support the video.
      </video>
      {!isAutoplayHeaderVideo && (
        <>
          {showGiantPlayButton && (
            <button
              className="play-button"
              onClick={onBigPlayButtonClick}
              type="button"
              aria-label="Play"
            >
              <Play width={50} height={50} color={theme === 'dark' ? 'black' : 'white'} />
            </button>
          )}
          {isShowingCoverPicture && (
            <Image className="cover-image" src={largeUrl} alt="video-cover" sizes="80vw" />
          )}
          <div className="controls-container">
            <SpaceFillingVideo.ControlsContainer $isVisible={!showGiantPlayButton && showControls}>
              <div className="controls">
                <SpaceFillingVideo.Progress
                  className="progress"
                  type="range"
                  min={0}
                  max={Math.round(duration * 10) / 10}
                  value={Math.round(progress * 10) / 10}
                  step={0.1}
                  onChange={handleSeekChange}
                  onMouseUp={handleSeekMouseUp}
                  onKeyUp={handleSeekMouseUp}
                  onTouchEnd={handleSeekMouseUp}
                  $theme={theme}
                />
                <div className="controls-group">
                  <div style={{ display: 'flex' }}>
                    <button
                      aria-label={isPlaying ? 'pause' : 'play'}
                      data-qa={isPlaying ? 'audio_pause_button' : 'audio_play_button'}
                      onClick={isPlaying ? onPause : onPlay}
                      type="button"
                      style={{ padding: 0 }}
                    >
                      {isPlaying ? (
                        <Pause
                          width={15}
                          height={15}
                          color={theme === 'dark' ? 'black' : 'white'}
                        />
                      ) : (
                        <Play width={15} height={15} color={theme === 'dark' ? 'black' : 'white'} />
                      )}
                    </button>
                    <VolumeControl
                      handleVolumeChange={handleVolumeChange}
                      handleVolumeIconClick={handleVolumeIconClick}
                      showVolumeControl={showVolumeControl}
                      theme={theme || 'light'}
                      volume={volume}
                    />
                    <div className="time-container">{getTimeDisplay(progress)}</div>
                  </div>
                </div>
              </div>
            </SpaceFillingVideo.ControlsContainer>
          </div>
        </>
      )}
    </SpaceFillingVideo.Container>
  );
};

SpaceFillingVideo.Container = styled.div<{ $theme: string }>`
  align-items: center;
  color: ${({ $theme }) => ($theme === 'dark' ? 'black' : 'white')};
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;

  height: 100%;
  width: 100%;
  max-height: 100%;
  max-width: 100%;
  min-height: 100%;
  min-width: 100%;

  video {
    max-width: none;
    min-width: 100vw;
    min-height: calc(100vh - 50px);
    object-fit: cover;
  }

  button {
    display: flex;
    justify-content: center;
    align-items: center;
    background: none;
    border: none;
    outline: none;
  }

  .cover-image {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 1;
    height: 100%;
    width: 100%;
    object-fit: cover;
  }

  .controls {
    width: 100%;
    z-index: ${VIDEO_CONTROLS_Z_INDEX};
  }

  .controls-group {
    display: flex;
    justify-content: space-between;
    margin-top: 10px;
  }

  .fullscreen-button-wrapper {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    width: 100%;
  }

  .fullscreen-button {
    height: auto;
  }

  .time-container {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    width: 100%;
    margin-left: 15px;
    ${variableFontRegular({ size: '11.5px', height: '16px', spacing: '0', fontWeight: '700' })};
  }

  .play-button {
    height: auto;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    z-index: ${PAUSE_BUTTON_Z_INDEX};
  }

  .controls-container {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;
    margin-bottom: 20px;
    padding: 0 20px;
    opacity: 50%;

    &:hover {
      opacity: 1;
    }
  }
`;

const thumbStyle = css<{ $theme: string }>`
  -webkit-appearance: none;
  appearance: none;
  height: 10px;
  width: 10px;
  background-color: ${({ $theme }) => ($theme === 'dark' ? 'black' : 'white')};
  border-radius: 50%;
  cursor: pointer;
`;

SpaceFillingVideo.Progress = styled.input<{ $theme: string }>`
  z-index: 7;
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 2px;
  background: ${({ $theme }) => ($theme === 'dark' ? 'black' : 'white')};
  padding: 0;
  border: none;

  ::-webkit-slider-thumb {
    ${thumbStyle};
  }

  ::-moz-range-thumb {
    ${thumbStyle};
  }
`;

SpaceFillingVideo.ControlsContainer = styled.div<{ $isVisible: boolean }>`
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: all 0.5s ease-in-out;
  width: 100%;
`;
